/**
 * TableTennisStatsCard
 *
 * Gamified table tennis career statistics card.
 * Reuses volleyball vb-* CSS classes for visual consistency.
 *
 * Props:
 *   stats  — object returned by teamService.getPlayerCareerStats()
 *   user   — { name, username, ... } from AuthContext
 */
import React from 'react';
import '../../../styles/components/sportStats.css';

const fmt = (n, decimals = 0) => (n == null ? '—' : Number(n).toFixed(decimals));
const perGame = (total, matches, dec = 2) =>
  matches > 0 ? fmt(total / matches, dec) : '—';
const perGameNum = (total, matches) => (matches > 0 ? total / matches : null);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

const LEVELS = [
  { min: 0,  max: 4,  label: 'ROOKIE',   color: '#94a3b8', next: 5  },
  { min: 5,  max: 14, label: 'AMATEUR',  color: '#4ade80', next: 15 },
  { min: 15, max: 29, label: 'PRO',      color: '#38bdf8', next: 30 },
  { min: 30, max: 49, label: 'ELITE',    color: '#a78bfa', next: 50 },
  { min: 50, max: Infinity, label: 'LEGEND', color: '#fbbf24', next: null },
];
const getLevel = (matches) => {
  const lvl = LEVELS.find((l) => matches >= l.min && matches <= l.max) || LEVELS[0];
  const progress = lvl.next
    ? Math.min(100, ((matches - lvl.min) / (lvl.next - lvl.min)) * 100)
    : 100;
  return { ...lvl, progress };
};

// Attack profile donut: ratio of loop winners to other winner types
const AttackDonut = ({ loopWinners = 0, flickWinners = 0, thirdBall = 0, smashWinners = 0 }) => {
  const total = Math.max(loopWinners + flickWinners + thirdBall + smashWinners, 1);
  const loopPct = loopWinners / total;
  const loopPercent = (loopPct * 100).toFixed(1);
  const r = 44; const cx = 56; const cy = 56;
  const circ = 2 * Math.PI * r;
  const filled = circ * loopPct;
  const ringColor = parseFloat(loopPercent) >= 50 ? '#f59e0b' : '#38bdf8';

  return (
    <div className="vb-donut-wrapper">
      <div className="vb-donut-ring-wrap">
        <svg viewBox="0 0 112 112" className="vb-donut-svg">
          <defs>
            <filter id="ttRingGlow">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13"/>
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth="13"
            strokeDasharray={`${filled} ${circ - filled}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"
            filter="url(#ttRingGlow)"/>
          <text x={cx} y={cy - 5} textAnchor="middle" className="vb-donut-main">{loopPercent}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" className="vb-donut-sub">LOOP%</text>
        </svg>
      </div>
      <div className="vb-donut-legend">
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background: ringColor}}/>
          <span className="vb-donut-text">LOOP: <strong className="vb-donut-val">{loopWinners}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(56,189,248,0.6)'}}/>
          <span className="vb-donut-text">FLICK: <strong className="vb-donut-val">{flickWinners}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(72,187,120,0.6)'}}/>
          <span className="vb-donut-text">3RD-BALL: <strong className="vb-donut-val">{thirdBall}</strong></span>
        </div>
      </div>
    </div>
  );
};

const StatSpot = ({ icon, label, value }) => (
  <div className="vb-spot">
    <div className="vb-spot__icon">{icon}</div>
    <div className="vb-spot__value">{value}</div>
    <div className="vb-spot__label">{label}</div>
  </div>
);

const TrophyCard = ({ icon, label, value, detail, rank }) => {
  const colors = { gold: '#fbbf24', silver: '#94a3b8', bronze: '#cd7f32' };
  const c = colors[rank] || 'rgba(255,255,255,0.2)';
  return (
    <div className="vb-trophy" style={{'--trophy-color': c}}>
      <div className="vb-trophy__icon">{icon}</div>
      <div className="vb-trophy__body">
        <div className="vb-trophy__label">{label}</div>
        <div className="vb-trophy__value">{value ?? '—'}</div>
        {detail && <div className="vb-trophy__detail">{detail}</div>}
      </div>
      {value != null && <div className="vb-trophy__badge">PB</div>}
    </div>
  );
};

const BarRow = ({ label, value, rawNum, maxRef }) => {
  const pct = rawNum != null && maxRef > 0 ? Math.min(100, (rawNum / maxRef) * 100) : 0;
  return (
    <div className="vb-bar-row">
      <div className="vb-bar-header">
        <span className="vb-bar-label">{label}</span>
        <span className="vb-bar-val">{value}</span>
      </div>
      <div className="vb-bar-track">
        <div className="vb-bar-fill" style={{width: `${pct}%`}}/>
      </div>
    </div>
  );
};

const MatchChip = ({ match }) => {
  const loops   = match.metrics?.forehandLoopWinner || 0;
  const flicks  = match.metrics?.flickWinner        || 0;
  const thirdBall= match.metrics?.thirdBallAttack   || 0;
  const deuces  = match.metrics?.deuceWin           || 0;
  const date    = formatDate(match.matchDate);
  const resultIcon = match.result === 'win' ? '🏆' : match.result === 'loss' ? '❌' : match.result === 'draw' ? '➖' : null;
  const displayTitle = match.matchTitle || (match.matchShortCode ? `#${match.matchShortCode}` : null) || `Match ${match.matchId}`;
  return (
    <div className="vb-match-chip">
      <div className="vb-match-chip__header">
        <div className="vb-match-chip__title">{displayTitle}</div>
        {resultIcon && <span className="vb-match-chip__result">{resultIcon}</span>}
      </div>
      <div className="vb-match-chip__date">{date || '—'}</div>
      <div className="vb-match-chip__stats">
        <span className="vb-chip-stat"><span className="vb-chip-ico">➰</span>{loops}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">⚡</span>{flicks}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🔄</span>{thirdBall}</span>
        {deuces > 0 && (
          <span className="vb-chip-stat vb-chip-pts"><span className="vb-chip-ico">🎯</span>{deuces}D</span>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const TableTennisStatsCard = ({ stats = {}, user = {}, sportProfile = {} }) => {
  const {
    careerTotals  = {},
    careerHighs   = {},
    matchesPlayed  = 0,
    winsCount      = 0,
    lossesCount    = 0,
    drawsCount     = 0,
    scoreContribution = 0,
    recentMatches  = [],
  } = stats;

  const loopWinners  = careerTotals.forehandLoopWinner || 0;
  const flickWinners = careerTotals.flickWinner        || 0;
  const thirdBall    = careerTotals.thirdBallAttack    || 0;
  const smashWinners = careerTotals.smashWinner        || 0;
  const blockWinners = careerTotals.blockWinner        || 0;
  const serviceAces  = careerTotals.serviceAce         || 0;
  const deuceWins    = careerTotals.deuceWin           || 0;
  const unforcedErrors = careerTotals.unforcedError    || 0;
  const totalWinners = loopWinners + flickWinners + thirdBall + smashWinners + blockWinners + serviceAces;
  const points       = scoreContribution || totalWinners;

  const level   = getLevel(matchesPlayed);
  const hasStats = matchesPlayed > 0;

  const lps  = perGameNum(loopWinners,  matchesPlayed);
  const fps  = perGameNum(flickWinners, matchesPlayed);
  const tps  = perGameNum(thirdBall,    matchesPlayed);
  const sps  = perGameNum(smashWinners, matchesPlayed);
  const aps  = perGameNum(serviceAces,  matchesPlayed);
  const maxBar = Math.max(lps||0, fps||0, tps||0, sps||0, aps||0, 1);

  const high = (key) => {
    const h = careerHighs[key];
    if (!h) return { value: null, detail: null };
    return {
      value:  h.value,
      detail: h.matchDate ? `vs. ${h.matchTitle || 'match'} · ${formatDate(h.matchDate)}` : null,
    };
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim().toUpperCase()
    : (user?.username || 'PLAYER').toUpperCase();

  return (
    <div className="vb-card">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="vb-hero">
        <div className="vb-hero__bg-orbs" aria-hidden="true">
          <span className="vb-orb vb-orb--1"/>
          <span className="vb-orb vb-orb--2"/>
        </div>
        <div className="vb-hero__ball" aria-hidden="true">🏓</div>
        <div className="vb-hero__info">
          <div className="vb-hero__name">{displayName}</div>
          <div className="vb-hero__sport">Table Tennis · Career Statistics</div>
          {(sportProfile?.position || sportProfile?.playingStyle) && (
            <div className="vb-hero__profile">
              {sportProfile.position     && <span className="vb-hero__position">📍 {sportProfile.position}</span>}
              {sportProfile.playingStyle && <span className="vb-hero__style">🎮 {sportProfile.playingStyle}</span>}
            </div>
          )}
        </div>
        <div className="vb-hero__meta">
          <div className="vb-level-badge" style={{'--lv-color': level.color}}>
            <span className="vb-level-badge__label">LEVEL</span>
            <span className="vb-level-badge__name">{level.label}</span>
          </div>
          <div className="vb-hero__matches">
            <span className="vb-hero__matches-num">{matchesPlayed}</span>
            <span className="vb-hero__matches-label">{' '}Match{matchesPlayed !== 1 ? 'es' : ''} Played</span>
          </div>
          {(winsCount > 0 || lossesCount > 0) && (
            <div className="vb-hero__record">
              <span className="vb-hero__win">🏆 {winsCount}W</span>
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__loss">❌ {lossesCount}L</span>
              {drawsCount > 0 && <><span className="vb-hero__sep"> · </span><span className="vb-hero__draw">➖ {drawsCount}D</span></>}
            </div>
          )}
        </div>
        <div className="vb-xp-bar-wrap">
          <div className="vb-xp-bar-track">
            <div className="vb-xp-bar-fill" style={{width: `${level.progress}%`}}/>
          </div>
          <span className="vb-xp-label">
            {level.next ? `${matchesPlayed} / ${level.next} matches to ${LEVELS.find(l => l.min === level.next)?.label || 'MAX'}` : '🏆 MAX LEVEL'}
          </span>
        </div>
      </div>

      {/* ── CORE STAT SPOTLIGHTS ─────────────────────────────────────────── */}
      <div className="vb-spots-row">
        <StatSpot icon="➰" label="LOOPS"     value={hasStats ? loopWinners  : '—'}/>
        <StatSpot icon="⚡" label="FLICKS"    value={hasStats ? flickWinners : '—'}/>
        <StatSpot icon="🔄" label="3RD BALL"  value={hasStats ? thirdBall    : '—'}/>
        <StatSpot icon="🎯" label="DEUCE W"   value={hasStats ? deuceWins    : '—'}/>
        <StatSpot icon="🏓" label="SVC ACES"  value={hasStats ? serviceAces  : '—'}/>
        <StatSpot icon="🏆" label="POINTS"    value={hasStats ? points       : '—'}/>
      </div>

      <div className="vb-card__body">
        {/* LEFT col */}
        <div className="vb-card__left">
          <h4 className="vb-section-title">🥇 CAREER HIGHS</h4>
          <div className="vb-trophies">
            <TrophyCard icon="➰" label="LOOP WINNERS"  rank="gold"   {...high('forehandLoopWinner')}/>
            <TrophyCard icon="⚡" label="FLICK WINNERS" rank="silver" {...high('flickWinner')}/>
            <TrophyCard icon="🔄" label="3RD BALL ATK"  rank="bronze" {...high('thirdBallAttack')}/>
            <TrophyCard icon="🎯" label="DEUCE WINS"    rank="gold"   {...high('deuceWin')}/>
            <TrophyCard icon="💥" label="SMASH WINNERS" rank="silver" {...high('smashWinner')}/>
            <TrophyCard icon="🏓" label="SERVICE ACES"  rank="bronze" {...high('serviceAce')}/>
          </div>
        </div>

        {/* RIGHT col */}
        <div className="vb-card__right">
          <h4 className="vb-section-title">📊 ATTACK PROFILE</h4>
          <AttackDonut
            loopWinners={loopWinners}
            flickWinners={flickWinners}
            thirdBall={thirdBall}
            smashWinners={smashWinners}
          />

          <h4 className="vb-section-title vb-section-title--mt">📈 PER-MATCH AVERAGES</h4>
          <div className="vb-bars">
            <BarRow label="Loop W / Match"   value={perGame(loopWinners,  matchesPlayed)} rawNum={lps} maxRef={maxBar}/>
            <BarRow label="Flick W / Match"  value={perGame(flickWinners, matchesPlayed)} rawNum={fps} maxRef={maxBar}/>
            <BarRow label="3rd Ball / Match" value={perGame(thirdBall,    matchesPlayed)} rawNum={tps} maxRef={maxBar}/>
            <BarRow label="Smash W / Match"  value={perGame(smashWinners, matchesPlayed)} rawNum={sps} maxRef={maxBar}/>
            <BarRow label="Aces / Match"     value={perGame(serviceAces,  matchesPlayed)} rawNum={aps} maxRef={maxBar}/>
          </div>

          <div className="vb-totals-row">
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(totalWinners)}</span><span className="vb-total-lbl">Winners</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(deuceWins)}</span><span className="vb-total-lbl">Deuce W</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(unforcedErrors)}</span><span className="vb-total-lbl">UEs</span></div>
          </div>
        </div>
      </div>

      {recentMatches.length > 0 && (
        <div className="vb-recent">
          <h4 className="vb-section-title vb-section-title--center">🕹️ RECENT MATCHES</h4>
          <div className="vb-match-list">
            {recentMatches.slice(0, 5).map((m) => (
              <MatchChip key={m.matchId} match={m}/>
            ))}
          </div>
        </div>
      )}

      {!hasStats && (
        <div className="vb-empty">
          <div className="vb-empty__ball">🏓</div>
          <div className="vb-empty__heading">YOUR JOURNEY BEGINS HERE</div>
          <p className="vb-empty__sub">No advanced scoring stats recorded yet.</p>
          <p className="vb-empty__cta">
            Play a live match in <strong>Advanced Scoring</strong> mode to start
            unlocking your table tennis career statistics.
          </p>
          <div className="vb-empty__unlock-chips">
            <span className="vb-unlock-chip">➰ Track Loops</span>
            <span className="vb-unlock-chip">⚡ Track Flicks</span>
            <span className="vb-unlock-chip">🎯 Deuce Wins</span>
            <span className="vb-unlock-chip">📊 Attack Profile</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableTennisStatsCard;
